/**
 * View: Dashboard (home).
 * Mostra status da plataforma + bloco "Renovações próximas" + atalhos.
 */

import { api } from '../../lib/api.js';
import { getUsuarioLogado } from '../../lib/auth.js';
import { fmtDateTime } from '../../lib/formatters.js';
import { escapeHtml, renderLayout } from '../layout.js';

function fmtBRL(n: number): string {
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

interface ContratoVencendoItem {
  codigoOmie: number;
  numero: string;
  diasRestantes: number;
  dataTermino: string;
  valorMensal: number;
  cliente: { cnpj: string; empresa: string };
}

function urgenciaClass(dias: number): 'critico' | 'alerta' | 'info' {
  if (dias <= 15) return 'critico';
  if (dias <= 45) return 'alerta';
  return 'info';
}

function urgenciaLabel(dias: number): string {
  if (dias < 0) return `Venceu há ${Math.abs(dias)}d`;
  if (dias === 0) return 'Vence hoje';
  if (dias === 1) return 'Vence amanhã';
  return `${dias}d`;
}

/** Renderiza tabela de contratos vencendo agrupada por urgência. */
function renderTabelaRenovacoes(contratos: ContratoVencendoItem[]): string {
  if (contratos.length === 0) {
    return `<p style="color:var(--color-text-muted);margin:0">Nenhum contrato vencendo nos próximos 90 dias.</p>`;
  }

  const grupos: Record<string, ContratoVencendoItem[]> = {
    critico: contratos.filter((c) => urgenciaClass(c.diasRestantes) === 'critico'),
    alerta: contratos.filter((c) => urgenciaClass(c.diasRestantes) === 'alerta'),
    info: contratos.filter((c) => urgenciaClass(c.diasRestantes) === 'info'),
  };

  const tituloGrupo: Record<string, string> = {
    critico: '🔴 Crítico — ≤ 15 dias',
    alerta: '🟡 Alerta — ≤ 45 dias',
    info: '🔵 Próximos — ≤ 90 dias',
  };

  const totalMensalGeral = contratos.reduce((s, c) => s + c.valorMensal, 0);

  let html = `
    <div class="ren-resumo">
      <span><strong>${contratos.length}</strong> contratos vencendo</span>
      <span>Receita mensal em risco: <strong>${fmtBRL(totalMensalGeral)}</strong></span>
    </div>
  `;

  for (const key of ['critico', 'alerta', 'info'] as const) {
    const lista = grupos[key];
    if (!lista || lista.length === 0) continue;
    const totalGrupo = lista.reduce((s, c) => s + c.valorMensal, 0);
    html += `
      <div class="ren-grupo ren-${key}">
        <div class="ren-grupo-titulo">
          <span>${tituloGrupo[key]} (${lista.length})</span>
          <span class="ren-grupo-total">${fmtBRL(totalGrupo)}/mês</span>
        </div>
        <table class="ren-table">
          <thead>
            <tr>
              <th>Cliente</th>
              <th>Contrato Omie</th>
              <th>Vencimento</th>
              <th>Em</th>
              <th>R$/mês</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            ${lista
              .map((c) => {
                const url = `/propostas/nova?tipo=renovacao-recorrente&cnpj=${encodeURIComponent(c.cliente.cnpj)}&contratoOmie=${encodeURIComponent(String(c.codigoOmie))}`;
                return `
                  <tr>
                    <td>${escapeHtml(c.cliente.empresa)}</td>
                    <td class="ren-mono">${escapeHtml(c.numero)}</td>
                    <td class="ren-mono">${escapeHtml(c.dataTermino)}</td>
                    <td class="ren-urg-${urgenciaClass(c.diasRestantes)}">${urgenciaLabel(c.diasRestantes)}</td>
                    <td class="ren-valor">${fmtBRL(c.valorMensal)}</td>
                    <td><a href="${url}" data-route class="btn btn-sm btn-primary">Renovar</a></td>
                  </tr>
                `;
              })
              .join('')}
          </tbody>
        </table>
      </div>
    `;
  }
  return html;
}

let renStylesInjected = false;
function injectRenovacoesStyles(): void {
  if (renStylesInjected) return;
  renStylesInjected = true;
  const style = document.createElement('style');
  style.textContent = `
    .ren-resumo {
      display: flex;
      justify-content: space-between;
      gap: var(--sp-4);
      padding: var(--sp-3) var(--sp-4);
      background: var(--color-surface-alt);
      border-radius: 6px;
      margin-bottom: var(--sp-4);
      font-size: var(--fs-sm);
    }
    .ren-grupo { margin-bottom: var(--sp-4); }
    .ren-grupo-titulo {
      display: flex;
      justify-content: space-between;
      align-items: center;
      font-weight: 600;
      font-size: var(--fs-sm);
      margin-bottom: var(--sp-2);
      padding: var(--sp-2) var(--sp-3);
      border-left: 4px solid transparent;
      border-radius: 0 4px 4px 0;
    }
    .ren-critico .ren-grupo-titulo { border-left-color: #c53030; background: #fff5f5; color: #742a2a; }
    .ren-alerta .ren-grupo-titulo { border-left-color: #d69e2e; background: #fffbeb; color: #744210; }
    .ren-info .ren-grupo-titulo { border-left-color: #3182ce; background: #ebf8ff; color: #2a4365; }
    .ren-grupo-total { font-family: var(--font-mono); font-size: var(--fs-xs); font-weight: 500; }
    .ren-table {
      width: 100%;
      border-collapse: collapse;
      font-size: var(--fs-sm);
    }
    .ren-table th, .ren-table td {
      padding: var(--sp-2) var(--sp-3);
      text-align: left;
      border-bottom: 1px solid var(--color-border);
    }
    .ren-table th {
      font-weight: 600;
      color: var(--color-text-muted);
      font-size: var(--fs-xs);
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }
    .ren-table tr:hover { background: var(--color-surface-alt); }
    .ren-mono { font-family: var(--font-mono); font-size: var(--fs-xs); }
    .ren-valor { font-family: var(--font-mono); text-align: right; font-weight: 500; }
    .ren-urg-critico { color: #c53030; font-weight: 600; }
    .ren-urg-alerta { color: #d69e2e; font-weight: 600; }
    .ren-urg-info { color: #3182ce; }
    .btn-sm { padding: 4px 12px; font-size: var(--fs-xs); }
  `;
  document.head.appendChild(style);
}

async function carregarRenovacoes(): Promise<void> {
  const $card = document.getElementById('renovacoes-card');
  if (!$card) return;
  try {
    const resp = await api.contratos.vencendo(90, false);
    $card.innerHTML = `
      <div class="card-title">📅 Renovações próximas (90 dias)</div>
      ${renderTabelaRenovacoes(resp.contratos)}
    `;
  } catch (err) {
    $card.innerHTML = `
      <div class="card-title">📅 Renovações próximas</div>
      <p style="color:var(--color-error,#c00);font-size:var(--fs-sm)">
        Falha ao carregar: ${escapeHtml(err instanceof Error ? err.message : String(err))}
      </p>
    `;
  }
}

export async function renderDashboard(): Promise<void> {
  const $app = document.getElementById('app');
  if (!$app) return;

  $app.innerHTML = renderLayout({
    title: 'Dashboard',
    activeNav: 'dashboard',
    bodyHtml: `
      <h1 style="font-family: var(--font-display); font-size: var(--fs-3xl); margin-bottom: var(--sp-6)">
        Dashboard
      </h1>
      <section class="card" id="status-card">
        <div class="card-title">Status da plataforma</div>
        <div>Carregando…</div>
      </section>
      <section class="card" id="renovacoes-card" style="margin-top: var(--sp-4)">
        <div class="card-title">📅 Renovações próximas</div>
        <div style="color:var(--color-text-muted);font-size:var(--fs-sm)">Carregando contratos vencendo…</div>
      </section>
    `,
  });
  injectRenovacoesStyles();

  const usuario = getUsuarioLogado();
  const $status = document.getElementById('status-card');
  if (!$status) return;

  if (!usuario) {
    $status.innerHTML = `
      <div class="card-title">Acesso restrito</div>
      <p style="color: var(--color-text-secondary)">
        Faça login com sua conta Microsoft Infomatic para acessar as propostas.
      </p>
    `;
    return;
  }

  try {
    const health = await api.health();
    const checkBadges = Object.entries(health.checks)
      .map(
        ([name, status]) =>
          `<span class="badge badge-${status === 'ok' ? 'ok' : 'error'}">${escapeHtml(name)}: ${escapeHtml(status)}</span>`,
      )
      .join(' ');
    $status.innerHTML = `
      <div class="card-title">Status da plataforma</div>
      <div style="display: flex; gap: var(--sp-3); flex-wrap: wrap; align-items: center; margin-bottom: var(--sp-3)">
        <span class="badge badge-${health.status === 'ok' ? 'ok' : 'warn'}">${health.status.toUpperCase()}</span>
        ${checkBadges}
        <span id="clicksign-badge" class="badge" style="opacity:.6">clicksign: …</span>
      </div>
      <div style="color: var(--color-text-muted); font-size: var(--fs-xs)">
        Ambiente <strong>${escapeHtml(health.env)}</strong> · versão ${escapeHtml(health.version)} · resposta em ${health.durationMs}ms · ${fmtDateTime(health.ts)}
      </div>
      <div style="margin-top: var(--sp-6); display: flex; gap: var(--sp-3); flex-wrap: wrap">
        <a href="/propostas" data-route class="btn btn-primary">Ver propostas</a>
        <a href="/propostas/nova" data-route class="btn btn-secondary">Nova proposta</a>
      </div>
    `;
  } catch (err) {
    $status.innerHTML = `
      <div class="card-title">Status da plataforma</div>
      <div class="badge badge-error">Falha: ${escapeHtml(err instanceof Error ? err.message : 'erro desconhecido')}</div>
    `;
  }

  // Pinga o Clicksign em separado — não bloqueia o status nem o derruba se falhar (#263)
  void carregarStatusClicksign();

  // Carrega renovações em paralelo (não bloqueia status)
  void carregarRenovacoes();
}

/**
 * Preenche o badge "clicksign" do status-card chamando o health check
 * autenticado (#263). Detecta token defasado (bug #262) antes do cliente.
 */
async function carregarStatusClicksign(): Promise<void> {
  const $badge = document.getElementById('clicksign-badge');
  if (!$badge) return;
  try {
    const r = await api.admin.healthClicksign();
    if (r.status === 'ok') {
      $badge.className = 'badge badge-ok';
      $badge.style.opacity = '1';
      $badge.textContent = `clicksign: ok (${r.durationMs}ms)`;
      $badge.title = `Token válido · ${r.baseUrl}`;
    } else {
      const motivoLabel =
        r.motivo === 'token-invalido'
          ? 'token inválido — rotacionar secret no Key Vault'
          : r.motivo === 'inacessivel'
            ? 'API inacessível (rede/timeout)'
            : `erro HTTP ${r.httpStatus ?? '?'}`;
      $badge.className = 'badge badge-error';
      $badge.style.opacity = '1';
      $badge.textContent = `clicksign: FALHA`;
      $badge.title = `${motivoLabel} · ${r.baseUrl}`;
    }
  } catch (err) {
    $badge.className = 'badge badge-error';
    $badge.style.opacity = '1';
    $badge.textContent = 'clicksign: erro';
    $badge.title = err instanceof Error ? err.message : String(err);
  }
}
